import { ChangeDetectionStrategy, Component, computed, inject, resource, output } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import {
  EventqDataTable,
  EventqDataTableColumn,
  EventqDataTableRow,
  EventqLinedTab,
  EventqSortState,
} from '@ecam/eventq-components';
import { environment } from '../../../../../environments/environment';
import { AgentDataService } from '../../../../core/services/agent-data.service';
import { AgentDataSyncService } from '../../../../core/services/agent-data-sync.service';
import { BrowserTabService } from '../../../../core/services/browser-tab.service';
import { EventHandleService } from '../../../../core/services/event-handle.service';
import { autoReloadResource } from '../../../../core/utils/auto-reload-resource';
import { withPreviousResourceValue } from '../../../../core/utils/with-previous-resource-value';
import { activeQueueSort, queueTableColumn } from '../../queue-table-sort';

@Component({
  selector: 'app-parked-events-section',
  imports: [EventqLinedTab, EventqDataTable],
  templateUrl: './parked-events-section.html',
  styleUrl: './parked-events-section.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ParkedEventsSection {
  private readonly agentData = inject(AgentDataService);
  private readonly browserTabs = inject(BrowserTabService);
  private readonly eventHandles = inject(EventHandleService);
  private readonly dataSync = inject(AgentDataSyncService);

  readonly eventRowActivate = output<string>();

  readonly sort = computed(() => activeQueueSort(this.browserTabs.parkedSort()));
  readonly disabledRowIds = computed(() => this.eventHandles.blockedOpenEventIds());

  readonly columns: EventqDataTableColumn[] = [
    queueTableColumn({ key: 'firstAlarmDetected', label: 'First Alarm Detected', grow: 1.2 }),
    queueTableColumn({ key: 'location', label: 'Location/Event', grow: 1.6 }),
    queueTableColumn({ key: 'parkedAt', label: 'Parked At', grow: 1.2 }),
    queueTableColumn({ key: 'parkedBy', label: 'Parked By', grow: 1 }),
  ];

  private readonly parkedSource = resource({
    params: () => ({
      sort: this.sort(),
      sync: this.dataSync.revisionFor('parked'),
    }),
    loader: ({ params }) =>
      firstValueFrom(this.agentData.getEventsParked(params.sort ?? undefined)).then((response) => {
        if (!params.sort?.column) {
          return response;
        }
        const items = [...response.items].sort((a, b) => {
          const left = a[params.sort!.column as keyof typeof a];
          const right = b[params.sort!.column as keyof typeof b];
          const result = String(left).localeCompare(String(right));
          return params.sort!.direction === 'desc' ? -result : result;
        });
        return { totalCount: items.length, items };
      }),
  });

  readonly parked = withPreviousResourceValue(this.parkedSource);
  readonly totalCount = computed(() => this.parked.value()?.totalCount ?? 0);

  constructor() {
    this.dataSync.watchWhileAlive('parked');
    autoReloadResource(this.parkedSource, environment.eventRefreshIntervalMs);
  }

  onSort(sort: EventqSortState): void {
    const next = activeQueueSort(sort);
    if (!next) {
      return;
    }
    this.browserTabs.updateActivePageState({ parkedSort: next });
  }

  onRowClick(row: EventqDataTableRow): void {
    const eventId = String(row['id'] ?? '');
    if (!eventId || this.eventHandles.blockedOpenEventIds().includes(eventId)) {
      return;
    }
    this.eventRowActivate.emit(eventId);
  }
}
